<script setup>
import useGrid from "@/composables/useGrid";
import {inject, onMounted, reactive, ref, watch} from "vue";
import startupsService from "@/services/ecosystem/startups.service";
import TablePager from "@/components/TablePager.vue";
import {useSingleRowItems} from "@/composables/useSingleRowItems";
import {getLogo} from "@/helpers/logo";
import {startupStatusClasses, startupStatusLabels} from "@/constants/needs";
import SubmittedNeedsModal from "@/views/ecosystem/myStartups/SubmittedNeedsModal.vue";

const emit = defineEmits(['addStartup']);

const {gridParams, gridMethods, items} = useGrid(getStartups, 5);
const {rowRef} = useSingleRowItems();

const hasStartups = inject('hasStartups');

const submittedNeeds = ref(null);
const filters = reactive({
    search: '',
});

watch(filters, () => {
    gridMethods.updateList();
});

onMounted(() => {
    if (hasStartups.value) {
        gridMethods.resetList();
    }
})

function applySearch(search) {
    filters.search = search;
}

async function getStartups(pageParams) {
    pageParams.filters = filters;

    return startupsService.getMyStartups(pageParams);
}
</script>

<template>
    <div class="dashboard-page__card dashboard-page__card--round">
        <div class="dashboard-page__card__inner">
            <header class="dashboard-page__card__header">
                <h3 class="dashboard-page__title">My Startups</h3>

                <div v-if="hasStartups" class="ml-auto">
                    <input v-debounce:500ms="applySearch" type="text" placeholder="Search for a startup or category" class="dashboard-page__search dashboard-page__search--md">
                </div>
            </header>
        </div>

        <div v-if="hasStartups" class="dashboard-page__table dashboard-page__table-flexible">
            <table>
                <thead>
                <tr>
                    <th>Company</th>
                    <th>Stage</th>
                    <th>Categories</th>
                    <th>Submitted</th>
                    <th>Status</th>
                </tr>
                </thead>

                <tbody>
                <tr v-for="startup in items" :key="startup.id">
                    <td>
                        <div class="dashboard-page__table__title">
                            <div class="data-labels-set data-labels-set--xs">
                                <span class="data-label">
                                    <img :src="getLogo(startup.logo)"/>
                                </span>
                            </div>
                            <div>
                                <h6 class="heading6"><a :href="`/startups/${startup.id}`">{{startup.name}}</a></h6>
                                <p>{{ startup.short_description }}</p>
                            </div>
                        </div>
                    </td>
                    <td>
                        {{ startup.stage }}
                    </td>
                    <td>
                        <div
                            v-if="startup.categories.length"
                            class="status-plate-wrapper"
                            :ref="el => rowRef(el, startup.id)"
                        >
                                <span
                                    v-for="category in startup.categories"
                                    :key="category"
                                    data-row-item
                                    class="status-plate status-plate--unreviewed"
                                >
                                    {{ category }}
                                </span>
                        </div>
                    </td>
                    <td>
                        <a
                            v-if="startup.submitted_count"
                            class="link link--view"
                            @click="submittedNeeds.open(startup.id)"
                        >
                            {{startup.submitted_count}}
                        </a>
                    </td>
                    <td>
                        <div class="d-flex align-items-center">
                            <span
                                v-if="startup.submit_statuses.length"
                                class="status-plate status-plate--strong"
                                :class="startupStatusClasses[startup.submit_statuses[0]]">
                                {{ startupStatusLabels[startup.submit_statuses[0]] }}
                                <span v-if="startup.submit_statuses.length > 1">+{{startup.submit_statuses.length - 1}} other</span>
                            </span>
                        </div>
                    </td>
                </tr>
                </tbody>
            </table>

            <p v-if="!items.length && filters.search.length" class="text-center mt-2">No Startups Found</p>

            <TablePager
                v-if="items.length"
                :pages-count="gridParams.pagesCount"
                :init-page="gridParams.currentPage"
                @changePage="gridMethods.changePage"
            />
        </div>

        <div v-else class="dashboard-page__empty">
            <p>Start recommending your startups to enterprises</p>

            <button class="button button-primary button--lg" @click="emit('addStartup')">Add Startups</button>
        </div>

        <SubmittedNeedsModal ref="submittedNeeds"/>
    </div>
</template>

<style scoped>

</style>
